import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowUp,
  Lock,
  RotateCw,
  Sparkles,
  TrendingUp,
  User,
  Users,
  X,
} from "lucide-react";

/**
 * Player-facing explainer for how the singles & doubles ladders work. Full-screen
 * overlay opened from the Ladder section. Plain-language only — the underlying
 * rating engine (TrueSkill) is intentionally never named or shown to players.
 */
export function LadderGuide({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    // Lock background scroll while the guide is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 bg-bg overflow-y-auto cs-modal-overlay">
      {/* Sticky header */}
      <header className="sticky top-0 z-10 bg-bg/95 backdrop-blur border-b border-ink/10">
        <div className="max-w-2xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55">
            How the ladder works
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 -mr-1 flex items-center justify-center text-ink/55 hover:text-ink cs-tap"
          >
            <X size={20} />
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-8 pb-20">
        {/* Hero — staggered entrance on mount. */}
        <section className="mb-12">
          <h1
            className="cs-reveal font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[0.95] mb-3"
          >
            Win games.
            <br />
            Climb the ladder.
          </h1>
          <p
            className="cs-reveal text-ink/65 text-lg leading-relaxed"
            style={{ animationDelay: "0.1s" }}
          >
            Every club keeps a simple ranked list. You hold a{" "}
            <span className="text-ink font-semibold">position</span> on it — and
            you move up by beating other players. That's the whole idea.
          </p>

          <div className="cs-reveal mt-6" style={{ animationDelay: "0.2s" }}>
            <MiniLadder />
          </div>
        </section>

        {/* Two ladders */}
        <Section
          kicker="Two ladders"
          title="Singles and doubles are separate"
          icon={<Users size={18} />}
        >
          <p className="mb-4">
            They're different skills, so they get their own ladders. Winning a
            doubles night doesn't touch your singles position, and vice-versa.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <FormatCard
              icon={<User size={16} />}
              title="Singles"
              body="One-on-one. Beat your opponent and you climb past them."
            />
            <FormatCard
              icon={<Users size={16} />}
              title="Doubles"
              body="Two-on-two, but you're still ranked as an individual — not as a pair."
            />
          </div>
        </Section>

        {/* How you move */}
        <Section
          kicker="Moving up"
          title="It's about who you beat"
          icon={<TrendingUp size={18} />}
        >
          <ul className="space-y-3">
            <Bullet>
              <strong>Win and you rise, lose and you slip.</strong> Each recorded
              result nudges everyone who played.
            </Bullet>
            <Bullet>
              <strong>Beating stronger players counts for more.</strong> Topple
              someone well above you and you'll jump further than beating someone
              you were already expected to beat.
            </Bullet>
            <Bullet>
              <strong>The score line doesn't change your rank.</strong> 6–0 or
              7–5, a win is a win. (Scores are still saved as a record of the
              match.)
            </Bullet>
          </ul>
        </Section>

        {/* Where you start */}
        <Section
          kicker="Starting out"
          title="Your first spot is just a starting guess"
          icon={<Sparkles size={18} />}
        >
          <p className="mb-4">
            New players begin from their self-rated skill (or wherever an admin
            seeds them). Nothing's locked in:
          </p>
          <ul className="space-y-3">
            <Bullet>
              <strong>Your first handful of games move you the most</strong> as
              the ladder figures out where you really belong.
            </Bullet>
            <Bullet>
              <strong>Then it settles.</strong> Once you've played a bit, one off
              night won't tank your position.
            </Bullet>
            <Bullet>
              <strong>Fresh players sit low until they've played.</strong> A spot
              near the top is earned with games, not claimed on day one.
            </Bullet>
          </ul>
        </Section>

        {/* Doubles deep-dive */}
        <Section
          kicker="Doubles, specifically"
          title="You're ranked for your own play"
          icon={<Users size={18} />}
        >
          <p className="mb-4">
            Doubles is the tricky one — did the strong partner carry, or did the
            weaker one hold their own? The ladder works that out and gives each
            partner their fair share of a win or loss, rather than splitting it
            evenly.
          </p>
          <div className="border border-ink/15 rounded-lg p-4 bg-bg-2/40 flex gap-3">
            <div className="shrink-0 w-9 h-9 rounded-md bg-accent/15 text-ink flex items-center justify-center">
              <RotateCw size={18} />
            </div>
            <div>
              <div className="font-display font-bold text-sm mb-1">
                Tip: rotate partners
              </div>
              <p className="text-ink/65 text-sm leading-relaxed">
                Doubles rankings are fairest when partners change game to game
                (the classic 3-sets, 3-partners format). Play with the same
                partner every time and it's hard to tell who's driving the wins.
              </p>
            </div>
          </div>
        </Section>

        {/* Fair by design */}
        <Section
          kicker="Under the hood"
          title="Built to be fair"
          icon={<TrendingUp size={18} />}
        >
          <ul className="space-y-3">
            <Bullet>
              <strong>It accounts for how sure it is about you.</strong> Early on
              it moves you quickly; the more you play, the steadier your position
              becomes.
            </Bullet>
            <Bullet>
              <strong>Mistakes are fixable.</strong> An admin can undo or correct
              any recorded result — the ladder simply recalculates from the full
              history, so a fixed scorecard always lands you in the right place.
            </Bullet>
          </ul>
        </Section>

        {/* Privacy */}
        <Section
          kicker="What you see"
          title="Just your position — no secret number"
          icon={<Lock size={18} />}
        >
          <p>
            Behind the list is a rating we deliberately keep hidden — you only
            ever see the resulting order. Some clubs hide the whole ladder too;
            even then it quietly runs in the background to help balance teams and
            assign courts evenly.
          </p>
        </Section>

        <div className="mt-12 text-center">
          <button
            onClick={onClose}
            className="cs-tap px-6 py-3 bg-ink text-bg font-display font-bold text-sm rounded-lg hover:brightness-110"
          >
            Got it
          </button>
        </div>
      </main>
    </div>,
    document.body,
  );
}

// Reveal a section once it scrolls into view (rises + fades via .cs-inview).
function useInView() {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          obs.disconnect(); // reveal once, then stop watching
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

function Section({
  kicker,
  title,
  icon,
  children,
}: {
  kicker: string;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const { ref, inView } = useInView();
  return (
    <section ref={ref} className={"mb-11 cs-inview" + (inView ? " is-visible" : "")}>
      <div className="flex items-center gap-2 mb-2 text-ink/55">
        <span className="text-accent-dim">{icon}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em]">{kicker}</span>
      </div>
      <h2 className="font-display font-bold text-2xl tracking-tight mb-3">{title}</h2>
      <div className="text-ink/70 leading-relaxed">{children}</div>
    </section>
  );
}

function FormatCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="border border-ink/15 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-ink/70">{icon}</span>
        <span className="font-display font-bold text-sm">{title}</span>
      </div>
      <p className="text-ink/60 text-sm leading-snug">{body}</p>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
      <span>{children}</span>
    </li>
  );
}

// Decorative ladder snippet — shows the "you climbed a rung" idea at a glance.
function MiniLadder() {
  const rows = [
    { pos: 1, name: "Priya", you: false },
    { pos: 2, name: "Marcus", you: false },
    { pos: 3, name: "You", you: true },
    { pos: 4, name: "Dana", you: false },
  ];
  return (
    <div className="border-2 border-ink rounded-xl overflow-hidden shadow-sm">
      <div className="bg-ink text-bg px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] flex items-center justify-between">
        <span>Doubles ladder</span>
        <span className="text-bg/50">this week</span>
      </div>
      <ol className="divide-y divide-ink/10">
        {rows.map((r, i) => (
          <li
            key={r.pos}
            className={
              "cs-reveal flex items-center gap-3 px-4 py-2.5 " +
              (r.you ? "bg-accent/15" : "")
            }
            style={{ animationDelay: `${0.3 + i * 0.09}s` }}
          >
            <span className="w-5 text-right font-mono text-sm text-ink/45 tabular-nums">
              {r.pos}
            </span>
            <span
              className={
                "w-7 h-7 rounded-full flex items-center justify-center font-display font-bold text-xs " +
                (r.you ? "bg-accent text-ink" : "bg-bg-3 text-ink")
              }
            >
              {r.name[0]}
            </span>
            <span className={"flex-1 text-sm " + (r.you ? "font-bold" : "")}>
              {r.name}
            </span>
            {r.you && (
              <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-success">
                <ArrowUp size={12} className="cs-bob" /> climbed
              </span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
